/** @jsx jsx */
import { ClassNames, css, jsx } from "@emotion/core"
import React, { useRef, useMemo, useEffect, useState } from "react"
import { Fields } from "@cd2/forms"
import { Grid, IconButton, Placeholder, Spinner } from "@cd2/components"
import { PopoutInput } from "./PopoutInput"
import { doo } from "@cd2/tools/build/es6"
import { Frame } from "./Frame"
import { createStoreBuilder, useStoreState } from "../utils/stores"
import { icons } from "../utils/icons"
import { useTheme } from "@cd2/theme/build/es6"
import { formPadding } from "../utils/styleUtils"
import { sortBy } from "../utils/array"
import { DelayedRender } from "./DelayedRender"
import classNames from "classnames"
import { assert } from "../utils/assert"
import { LOADING_OPTION } from "./_NewAbstractSelectField"

interface INewAbstractSelectState {
  options: any[] | readonly any[] | typeof LOADING_OPTION
  query: string
}

type INewAbstractSelectActions =
  | { type: "queryChanged"; query: string }
  | { type: "optionsChanged"; payload: readonly any[] | typeof LOADING_OPTION }

const buildNewAbstractSelectStore = createStoreBuilder<
  INewAbstractSelectState,
  INewAbstractSelectActions
>({
  queryChanged(state, { query }) {
    state.query = query
  },
  optionsChanged(state, { payload }) {
    state.options = payload
  },
})

export type IRenderOptionInfo = {
  isRenderingInOptionList: boolean
}

type INewAbstractSelectFieldProps<T> = {
  keyExtractor(item: T): string
  value: T[] | string[] | null | undefined | typeof LOADING_OPTION
  options: T[] | readonly T[] | typeof LOADING_OPTION
  renderOption(item: T, info: IRenderOptionInfo): React.ReactNode
  filter?: {
    value: string
    onChange(value: string): void
    isLoading: boolean
  }
  filterActions?: React.ReactNode
  /**
   * Given an item and a query return if the item matches.
   *
   * If a boolean is returned then the order of items will be maintained.
   * Returning a number will cause the items to be sorted highest to lowest.
   * Negative results will be filtered out of the array.
   */
  filterOption?(item: T, query: string): boolean | number
  label?: React.ReactNode
  showRequiredStar?: boolean
  error?: string
  disabled?: boolean
  noSearch?: boolean
  placeholder?: string
  renderNoResults?(): React.ReactNode
  isClearable?: boolean
  onChange(item: T[]): void
  partClassNames?: {
    valueOption?(item: T): string
  }
  inline?: boolean
}

export function NewAbstractMultiSelectField<T>({
  value,
  options,
  renderOption,
  filter,
  filterActions,
  filterOption,
  onChange,
  label,
  showRequiredStar,
  keyExtractor,
  error,
  disabled,
  isClearable,
  noSearch,
  placeholder,
  renderNoResults,
  partClassNames,
  inline,
}: INewAbstractSelectFieldProps<T>) {
  const theme = useTheme()

  const store = useMemo(
    () =>
      buildNewAbstractSelectStore({
        query: "",
        options,
      }),
    [],
  )

  useEffect(() => {
    store.dispatch({ type: "optionsChanged", payload: options })
  }, [JSON.stringify(options)])
  const state = useStoreState(store)

  const queryInputRef = useRef<HTMLInputElement>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [focusIndex, setFocusIndex] = useState<number | null>(null)
  useEffect(() => {
    const el = document.querySelector(`[data-focus-index="${focusIndex}"]`)

    if (el) {
      el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" })
    }
  }, [focusIndex])

  useEffect(() => {
    if (focusIndex !== null) {
      setFocusIndex(null)
    }

    const raf = requestAnimationFrame(() => {
      if (isOpen && value && value !== LOADING_OPTION) {
        const selectedOption: HTMLElement | null = document.querySelector(
          `[data-option-key="${typeof value[0] === "string" ? value : keyExtractor(value[0])}"]`,
        )

        if (selectedOption) {
          // This first call acts on ALL scrollable containers, including the
          // body element. This means that if the popout is slightly off screen,
          // we will scroll the page to view it.
          selectedOption.scrollIntoView({ block: "nearest", inline: "nearest" })

          // This next check is specifically doing the popout scroll container.
          // We do this separately because we want the element positioned at the
          // top of the list.
          if (
            selectedOption.parentElement &&
            selectedOption.parentElement.scrollHeight > selectedOption.parentElement.clientHeight
          ) {
            selectedOption.parentElement.scrollTop =
              selectedOption.offsetTop - selectedOption.parentElement.offsetTop
          }
        }
      }
    })

    return () => cancelAnimationFrame(raf)
  }, [isOpen, state.options])

  function handleSelect(opt: T): void {
    if (!value) {
      onChange([opt])
    } else if (value === LOADING_OPTION || options === LOADING_OPTION) {
      return
    } else {
      const inflatedOptions = value
        .map(v => {
          if (typeof v === "string") {
            const foundOption = options?.find(opt => {
              const optKey = keyExtractor(opt)
              return optKey === v
            })
            return foundOption ?? null
          } else {
            return v
          }
        })
        .filter(Boolean)
      if (isOptionSelected(opt)) {
        onChange(inflatedOptions.filter(o => keyExtractor(o) !== keyExtractor(opt)))
      } else {
        onChange([...inflatedOptions, opt])
      }
    }
  }

  const mainContent = doo(() => {
    const selectedOptions = doo(() => {
      if (value === null || value === undefined) {
        return null
      } else if (value === LOADING_OPTION) {
        return LOADING_OPTION
      } else if (value.length === 0) {
        return null
      } else {
        return value
          .map(value => {
            if (typeof value === "string") {
              if (options === LOADING_OPTION) {
                return LOADING_OPTION
              }
              const foundOption = options?.find(opt => {
                const optKey = keyExtractor(opt)
                return optKey === value
              })
              return foundOption ?? null
            } else {
              return value
            }
          })
          .filter(Boolean)
      }
    })

    if (selectedOptions === null) {
      return (
        <span
          css={css`
            color: ${theme.forms.placeholderColor};
          `}
        >
          {placeholder ?? `Select...`}
        </span>
      )
    } else if (selectedOptions === LOADING_OPTION) {
      return <Placeholder />
    } else {
      return (
        <div
          css={css`
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
          `}
        >
          {selectedOptions.map(option =>
            option === LOADING_OPTION ? (
              <Placeholder key={"LOADING_OPT"} />
            ) : (
              <div
                key={keyExtractor(option)}
                css={css`
                  background: #efefef;
                  border-radius: 4px;
                  padding: 4px 8px;
                  cursor: pointer;
                  :hover {
                    background: #e2e2e2;
                  }
                `}
                className={partClassNames?.valueOption?.(option)}
                onClick={e => {
                  e.stopPropagation()
                  handleSelect(option)
                }}
              >
                {renderOption(option, { isRenderingInOptionList: false })}
              </div>
            ),
          )}
        </div>
      )
    }
  })

  function selectOptionsToRender(): T[] | readonly T[] | typeof LOADING_OPTION {
    let options = state.options

    if (filterOption && options !== LOADING_OPTION) {
      const optionsWithScore = options
        .map(opt => {
          let score = filterOption(opt, state.query)
          if (typeof score === "boolean") {
            score = score ? Number.MAX_SAFE_INTEGER : -1
          }
          return { opt, score }
        })
        .filter(({ score }) => score >= 0)

      options = sortBy(optionsWithScore, opt => opt.score)
        .map(({ opt }) => opt)
        .reverse()
    }

    return options
  }

  function handleOnShown() {
    setIsOpen(true)
  }
  function handleOnHidden() {
    setIsOpen(false)
  }

  function handleFocusPrevious() {
    if (!isOpen || !state.options || state.options === LOADING_OPTION) {
      return
    }

    let newFocus = null
    if (focusIndex === null) {
      newFocus = state.options.length - 1
    } else if (focusIndex === 0) {
      newFocus = state.options.length - 1
    } else {
      newFocus = focusIndex - 1
    }

    setFocusIndex(newFocus)
  }
  function handleFocusNext() {
    if (!isOpen || !state.options || state.options === LOADING_OPTION) {
      return
    }

    if (focusIndex === null) {
      setFocusIndex(0)
    } else if (focusIndex === state.options.length - 1) {
      setFocusIndex(0)
    } else {
      setFocusIndex(focusIndex + 1)
    }
  }
  function handleSelectFocussed() {
    if (!isOpen || !state.options || focusIndex === null || state.options === LOADING_OPTION) {
      return
    }

    handleSelect(state.options[focusIndex])
  }

  function isOptionSelected(option: T) {
    if (value === null || value === undefined || value === LOADING_OPTION) {
      return false
    } else {
      return value.some(value => {
        if (typeof value === "string") {
          return value === keyExtractor(option)
        } else {
          return keyExtractor(value) === keyExtractor(option)
        }
      })
    }
  }

  return (
    <PopoutInput
      label={label}
      showRequiredStar={showRequiredStar}
      error={error}
      disabled={disabled}
      onShown={handleOnShown}
      onHidden={handleOnHidden}
      inline={inline}
      extendTheme={{
        main: { padding: 0 },
      }}
      renderMain={() => (
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <div
            css={css`
              padding: ${formPadding(theme.forms.padding ?? { topAndBottom: 10, sides: 9 })};
              overflow: hidden;
              text-overflow: ellipsis;
              flex: 1;
            `}
          >
            {mainContent}
          </div>
          {isClearable && Array.isArray(value) && value.length > 0 && (
            <IconButton
              buttonStyle="text"
              color="#333333"
              padding={8}
              onClick={() => {
                // @ts-ignore this is only rendered if isClearable is true
                onChange([])
              }}
              icon={icons.x}
            />
          )}
        </div>
      )}
      renderOptions={() => (
        <div
          css={css`
            width: 100%;
          `}
        >
          {filter && (
            <Grid
              css={css`
                border-bottom: 1px solid #ccc;
              `}
              columns={filterActions ? ["auto", "max-content", 32] : ["auto", 32]}
              alignItems="center"
            >
              <Fields.Text
                ref={queryInputRef}
                value={filter.value}
                onChange={filter.onChange}
                placeholder="Search..."
                inputProps={{
                  autoFocus: true,
                  type: "search",
                  onFocus(e) {
                    e.target.select()
                  },
                  onKeyDown(e) {
                    if (e.key === "ArrowUp") {
                      e.preventDefault()
                      handleFocusPrevious()
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault()
                      handleFocusNext()
                    } else if (e.key === "Enter") {
                      e.preventDefault()
                      handleSelectFocussed()
                    }
                  },
                }}
                css={css`
                  border: none !important;
                  input {
                    border: none !important;
                  }
                `}
              />
              {filterActions && (
                <div
                  css={css`
                    padding-right: 8px;
                    white-space: nowrap;
                  `}
                >
                  {filterActions}
                </div>
              )}
              {filter.isLoading && (
                <DelayedRender>
                  <div
                    css={css`
                      justify-self: center;
                    `}
                  >
                    <Spinner color="black" size={24} />
                  </div>
                </DelayedRender>
              )}
            </Grid>
          )}
          {!noSearch && filterOption && (
            <div
              css={css`
                border-bottom: 1px solid #ccc;
                position: relative;
              `}
            >
              <Fields.Text
                ref={queryInputRef}
                value={state.query}
                onChange={value => store.dispatch({ type: "queryChanged", query: value })}
                placeholder="Search..."
                inputProps={{
                  autoFocus: true,
                  type: "search",
                  onFocus(e) {
                    e.target.select()
                  },
                  onKeyDown(e) {
                    if (e.key === "ArrowUp") {
                      e.preventDefault()
                      handleFocusPrevious()
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault()
                      handleFocusNext()
                    } else if (e.key === "Enter") {
                      e.preventDefault()
                      handleSelectFocussed()
                    }
                  },
                }}
                css={css`
                  border: none !important;
                  input {
                    border: none !important;
                  }
                `}
              />
            </div>
          )}

          {state.options === LOADING_OPTION && (
            <Frame
              minHeight={40}
              css={css`
                padding: 16px;
              `}
            >
              <Spinner color="black" />
            </Frame>
          )}
          {state.options &&
            state.options !== LOADING_OPTION &&
            state.options.length === 0 &&
            (renderNoResults ? (
              renderNoResults()
            ) : (
              <Frame
                minHeight={40}
                css={css`
                  color: grey;
                  padding: 16px;
                `}
              >
                No results
              </Frame>
            ))}
          {state.options && state.options !== LOADING_OPTION && state.options.length > 0 && (
            <div
              css={css`
                max-height: 300px;
                overflow-y: auto;
              `}
            >
              <ClassNames
                children={({ css }) => {
                  const options = selectOptionsToRender()

                  assert(options !== LOADING_OPTION, "options should not be loading here")

                  return options.map((opt, i) => {
                    const isSelected = isOptionSelected(opt)

                    return (
                      <Grid
                        key={keyExtractor(opt)}
                        columns={["max-content", "auto"]}
                        alignItems="center"
                        css={css`
                          transition: 0.1s;
                          cursor: pointer;

                          :hover {
                            background: #efefef;
                          }

                          ${isSelected &&
                          css`
                            background: #e6f5ff;
                          `}
                        `}
                      >
                        <div
                          css={css`
                            padding: 4px;
                          `}
                        >
                          <Fields.Checkbox value={isSelected} onChange={() => handleSelect(opt)} />
                        </div>
                        <React.Fragment>
                          <div
                            className={classNames(
                              css`
                                padding: 8px 12px;
                                padding-left: 0;

                                &.focused {
                                  background: #efefef;
                                }
                              `,

                              {
                                focused: focusIndex === i,
                              },
                            )}
                            onClick={() => handleSelect(opt)}
                            {...{
                              "data-focus-index": i,
                              "data-option-key": keyExtractor(opt),
                            }}
                          >
                            {renderOption(opt, {
                              isRenderingInOptionList: true,
                            })}
                          </div>
                        </React.Fragment>
                      </Grid>
                    )
                  })
                }}
              />
            </div>
          )}
        </div>
      )}
    />
  )
}
